import { createTheme, type ThemeOptions } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { button, form, layout, radius, surfaces, table, typography } from './tokens';

const heading = (fontSize: string) => ({
  fontSize,
  fontWeight: typography.weight.medium,
});

const sharedTypography: ThemeOptions['typography'] = {
  fontFamily: typography.fontFamily,
  body1: { fontSize: typography.size.md },
  body2: { fontSize: typography.size.sm },
  h1: heading(typography.size['5xl']),
  h2: heading(typography.size['4xl']),
  h3: heading(typography.size['3xl']),
  h4: heading(typography.size['2xl']),
  h5: heading(typography.size.xl),
  h6: heading(typography.size.lg),
};

/** Fixed button height. Vertical padding stays inside the box so MUI's line-height math cannot change it. */
const buttonMetrics = (height: number) => ({
  boxSizing: 'border-box' as const,
  height,
  minHeight: height,
  fontSize: form.fontSize,
  lineHeight: button.lineHeight,
  paddingTop: 0,
  paddingBottom: 0,
});

/** Same metrics on every size slot so variant-specific classes cannot override the height. */
const buttonSizeSlots = (
  size: 'Tiny' | 'Small' | 'Medium' | 'Large',
  height: number,
  extra?: { paddingLeft: number; paddingRight: number },
) => {
  const metrics = { ...buttonMetrics(height), ...extra };
  return {
    [`size${size}`]: metrics,
    [`textSize${size}`]: metrics,
    [`outlinedSize${size}`]: metrics,
    [`containedSize${size}`]: metrics,
  };
};

/** Shared control metrics — MUI core OutlinedInput + MUI X PickersOutlinedInput. */
const outlinedControlRootSx = {
  height: form.controlHeight,
  fontSize: form.fontSize,
  boxSizing: 'border-box' as const,
};

const sharedComponents: ThemeOptions['components'] = {
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        ...outlinedControlRootSx,
        '&.MuiInputBase-multiline': {
          height: 'auto',
        },
      },
      input: {
        padding: `${form.inputPaddingY} ${form.inputPaddingX}`,
        // Multiline padding lives on the root. This input rule must not add a second inset.
        '&.MuiInputBase-inputMultiline': {
          padding: 0,
        },
        // Hide browser number spinners by default (Chrome/Safari + Firefox).
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        MozAppearance: 'textfield',
      },
    },
  },
  // MUI X v8 uses PickersTextField / PickersOutlinedInput (not MuiOutlinedInput).
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        ...outlinedControlRootSx,
        // Beat MUI X size="small" sectionsContainer padding (8.5px) so height matches.
        '& .MuiPickersOutlinedInput-sectionsContainer': {
          paddingTop: form.inputPaddingY,
          paddingBottom: form.inputPaddingY,
        },
      },
      sectionsContainer: {
        paddingTop: form.inputPaddingY,
        paddingBottom: form.inputPaddingY,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: form.fontSize,
        minHeight: form.menuItemMinHeight,
        paddingTop: form.menuItemPaddingY,
        paddingBottom: form.menuItemPaddingY,
        paddingLeft: theme.spacing(form.menuItemPaddingX),
        paddingRight: theme.spacing(form.menuItemPaddingX),
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      label: {
        fontSize: form.labelFontSize,
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontSize: table.headerFontSize,
        fontWeight: table.headerFontWeight,
        lineHeight: table.headerLineHeight,
        letterSpacing: table.headerLetterSpacing,
        padding: `${table.headerPaddingY} ${table.headerPaddingX}`,
      },
      body: {
        fontSize: table.bodyFontSize,
        padding: `${table.bodyPaddingY} ${table.bodyPaddingX}`,
      },
      footer: {
        fontSize: table.paginationFontSize,
        padding: `${table.footerPaddingY} ${table.footerPaddingX}`,
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: {
        fontSize: table.paginationFontSize,
      },
      toolbar: {
        // Scoped to TablePagination only — does not affect AppBar MuiToolbar.
        minHeight: table.paginationMinHeight,
        height: table.paginationMinHeight,
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: table.footerPaddingX,
        paddingRight: table.footerPaddingX,
      },
      selectLabel: {
        fontSize: table.paginationFontSize,
      },
      displayedRows: {
        fontSize: table.paginationFontSize,
      },
      select: {
        fontSize: table.paginationFontSize,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        ...buttonMetrics(button.height),
        textTransform: 'none',
        fontWeight: button.fontWeight,
      },
      ...buttonSizeSlots('Tiny', button.heightTiny, {
        paddingLeft: button.paddingXTiny,
        paddingRight: button.paddingXTiny,
      }),
      ...buttonSizeSlots('Small', button.heightSmall),
      ...buttonSizeSlots('Medium', button.height),
      ...buttonSizeSlots('Large', button.heightLarge),
      ['iconSizeTiny' as 'iconSizeSmall']: {
        '& > *:nth-of-type(1)': {
          fontSize: button.iconSizeTiny,
        },
      },
    },
  },
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: layout.appBarHeight,
        height: layout.appBarHeight,
      },
    },
  },
};

const lightPalette: ThemeOptions['palette'] = {
  mode: 'light',
  primary: {
    main: '#1976d2',
    light: '#42a5f5',
    dark: '#1565c0',
  },
  secondary: {
    main: '#dc004e',
    light: '#ff5983',
    dark: '#9a0036',
  },
  background: {
    default: surfaces.appBackgroundColor,
    paper: '#ffffff',
  },
  text: {
    primary: '#333333',
    secondary: '#666666',
  },
};

const darkPalette: ThemeOptions['palette'] = {
  mode: 'dark',
  primary: {
    main: '#90caf9',
    light: '#e3f2fd',
    dark: '#42a5f5',
  },
  secondary: {
    main: '#f48fb1',
    light: '#f8bbd9',
    dark: '#ec407a',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
  },
  text: {
    primary: '#ffffff',
    secondary: '#b0b0b0',
  },
};

const modeComponents = (mode: 'light' | 'dark'): ThemeOptions['components'] => ({
  ...sharedComponents,
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'light' ? '#1976d2' : '#1e1e1e',
        color: '#ffffff',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      // MUI Drawer root defaults to zIndex.drawer; AppBar is drawer + 1, so
      // temporary full-viewport drawers must sit above AppBar (use modal).
      // Permanent/persistent (e.g. Sidebar) keep drawer z-index + own inset.
      root: ({ ownerState, theme }) =>
        (ownerState.variant ?? 'temporary') === 'temporary'
          ? { zIndex: theme.zIndex.modal }
          : {},
      paper: {
        backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
        color: mode === 'light' ? '#333333' : '#ffffff',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundColor: mode === 'light' ? '#ffffff' : '#1e1e1e',
        boxShadow: mode === 'light' ? '0 2px 8px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.3)',
      },
    },
  },
});

export const createAppTheme = (mode: 'light' | 'dark') =>
  createTheme({
    palette: mode === 'light' ? lightPalette : darkPalette,
    shape: {
      borderRadius: radius.base,
    },
    typography: sharedTypography,
    mixins: {
      toolbar: {
        minHeight: layout.appBarHeight,
        height: layout.appBarHeight,
      },
    },
    components: modeComponents(mode),
  });
